import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetEventCTAButtonsDataQuery } from '../../components/CtaButtons/CtaButtons.graphql.generated';
import { useGetRsvpDeadlineQuery } from '../../components/CtaButtons/RsvpDeadline.graphql.generated';

/**
 * Single source of truth for RSVP deadline gating across guest surfaces.
 *
 * The deadline query is keyed on the event id. We resolve it primarily from
 * `useEventInfo()` (`eventInfo.eventId`), which is seeded from the SSR-embedded
 * `__INITIAL_EVENT_ID__` and stays stable across renders. The handle-keyed
 * `GetEventCTAButtonsData` query (`eventByName.id`) is kept only as a fallback:
 * because it runs `ssr: false`, its result flickers between the id and
 * `undefined` across the many guest surfaces that mount this guard, and keying
 * on it alone made the deadline query re-skip mid-render — the deadline (and
 * the post-deadline button suppression) would drop out whenever it briefly
 * resolved to `undefined`. See LEGRSVP-44.
 */
export const useRsvpDeadlineGuard = () => {
  const { eventName } = useEventNameMatch();
  const { eventInfo } = useEventInfo();

  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    skip: !eventName,
    ssr: false,
  });
  const eventId = eventInfo?.eventId ?? sessionData?.eventByName?.id;

  const { data: deadlineData } = useGetRsvpDeadlineQuery({
    variables: { eventId: eventId || '' },
    batchMode: 'fast',
    skip: !eventId,
    ssr: false,
  });

  const rsvpDeadline = deadlineData?.getRsvpSettingsByEventId;
  // An event created with "still deciding" has no event date, so a deadline on it has nothing to
  // anchor and guests must never be blocked from RSVPing by it. That decision is made in
  // contact_service — `eventHasDate` is resolved over a trusted m2m call and already folded into
  // `isOpen` — so this hook only has to render it, not re-derive it.
  //
  // Do NOT reintroduce a client-side `eventInfo.finalizedDate == null` check here. That field
  // resolves through `tentativeDate()`, gated on `requirePasswordForEventDate`, so it is null for
  // every guest who has not unlocked a date-protected site — indistinguishable from a genuinely
  // dateless event, and it would silently switch the deadline off for all of them. Same gate that
  // made `deadlineTimezone` necessary below.
  //
  // `=== false` deliberately, not falsy: null means "not resolved" (no deadline configured, or the
  // gateway lookup failed), which must leave the deadline exactly as it was.
  const eventIsDateless = rsvpDeadline?.eventHasDate === false;
  const isRsvpClosedByDeadline = rsvpDeadline?.isOpen === false;
  // `deadlineEnabled` is checked first and separately from `showDeadline`: they are independent
  // columns, and `updateRsvpSettingsByEventId` strips nils via `pickBy`, so turning the deadline
  // off leaves `showDeadline` and `deadlineDate` behind. Without this gate a couple who set a
  // deadline, showed it, then switched it off still had "RSVP by <date>" rendered to guests for a
  // deadline that no longer applies — and, since contact_service skips the timezone lookup for a
  // disabled deadline, rendered it in UTC rather than event-local time.
  const showDeadlineToGuest = !eventIsDateless && rsvpDeadline?.deadlineEnabled === true && rsvpDeadline?.showDeadline === true && rsvpDeadline?.deadlineDate != null;

  const deadlineDate = rsvpDeadline?.deadlineDate ?? null;
  // Custom post-deadline note, surfaced only once RSVP is actually closed.
  // Null/empty means no override — guest surfaces render nothing in that case.
  const deadlineNote = isRsvpClosedByDeadline ? rsvpDeadline?.deadlineNote?.trim() || null : null;
  // Resolved server-side, ungated: the RSVP deadline is not covered by the event
  // date/location password gates, so the BE returns the event timezone even when
  // `EventInfo.timezone` is null for locked guests. Fall back to UTC only when
  // the BE couldn't resolve it (matches the pre-fix behavior for that case).
  const deadlineTimezone = rsvpDeadline?.deadlineTimezone ?? 'UTC';

  return { isRsvpClosedByDeadline, showDeadlineToGuest, deadlineDate, deadlineNote, deadlineTimezone };
};
